export function generateFaqSchema(faqs = []) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  }
}

export function generateFAQSchemaFromHtml(html) {
  const faqs = []
  if (process.client) {
    // DOMParser only available in browser
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')

    const questionElems = doc.querySelectorAll('[data-question]')
    questionElems.forEach(qEl => {
      const question = qEl.textContent.trim()
      const answerEl = qEl.nextElementSibling?.matches('[data-answer]')
        ? qEl.nextElementSibling
        : null
      const answer = answerEl ? answerEl.innerHTML.trim() : ''

      faqs.push({
        "@type": "Question",
        "name": question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": answer
        }
      })
    })
  }
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs
  }
}

export function generateFAQSchemaFromHtmlRegex(html) {
  const faqs = []
  const regex = /<h3[^>]*data-question[^>]*>(.*?)<\/h3>\s*<div[^>]*data-answer[^>]*>([\s\S]*?)<\/div>/gi
  let match

  while ((match = regex.exec(html)) !== null) {
    const question = match[1].replace(/<[^>]+>/g, '').trim()
    const answer = match[2].trim()
    faqs.push({
      "@type": "Question",
      "name": question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": answer // leave HTML or sanitize
      }
    })
  }

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs
  }
}

export function generateBreadcrumbSchema(crumbs = [], baseUrl) {
  const isAbsolute = (u) => /^https?:\/\//i.test(u);

  // Build a base, preferring an explicit argument, then env, then client origin
  const base =
    baseUrl ||
    (process.env.DOMAIN
      ? `https://${String(process.env.DOMAIN).replace(/^https?:\/\//, '')}`
      : '') ||
    (typeof window !== 'undefined' ? window.location.origin : '');

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": crumbs.map((crumb, index) => {
      const url = crumb?.url || '/';
      const item = isAbsolute(url)
        ? url
        : (base ? new URL(url, base).href : url); // if no base, leave as-is (relative)

      return {
        "@type": "ListItem",
        "position": index + 1,
        "name": crumb.name,
        "item": item
      };
    })
  };
}

export function generateHomepageSchema() {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://www.bet777.es/#organization",
        "name": "Bet777",
        "alternateName": [
          "Bet777 España",
          "Bet777 ES"
        ],
        "url": "https://www.bet777.es/",
        "logo": "https://www.bet777.es/images/logo.svg",
        "description": "Bet777 es una casa de apuestas online con licencia oficial en España. Disfruta de apuestas deportivas en un entorno seguro y confiable. Reconocida como una de las mejores casas de apuestas de España.",
        "sameAs": [
          "https://www.facebook.com/bet777es/",
          "https://x.com/bet777es",
          "https://www.instagram.com/bet777es/",
          "https://es.trustpilot.com/review/bet777.es"
        ],
        "contactPoint": {
          "@type": "ContactPoint",
          "contactType": "Atención al cliente",
          "email": "ayuda@777.es",
          "telephone": "+34-686-68-75-89",
          "areaServed": "ES",
          "availableLanguage": ["es"]
        },
        "subjectOf": [
          {
            "@type": "Thing",
            "@id": "https://es.wikipedia.org/wiki/Apuesta_deportiva",
            "name": "Apuesta deportiva"
          },
          {
            "@type": "Thing",
            "@id": "https://www.wikidata.org/wiki/Q1506462",
            "name": "Apuesta Deportiva"
          },
          {
            "@type": "Thing",
            "@id": "https://es.wikipedia.org/wiki/Apuestas_en_l%C3%ADnea",
            "name": "Apuestas en línea"
          },
          {
            "@type": "Thing",
            "@id": "https://en.wikipedia.org/wiki/Bookmaker",
            "name": "Casa de Apuestas"
          },
          {
            "@type": "Thing",
            "@id": "https://www.wikidata.org/wiki/Q664702",
            "name": "Casa de Apuestas"
          },
          {
            "@type": "Thing",
            "@id": "https://en.wikipedia.org/wiki/Online_gambling",
            "name": "Online Gambling"
          }
        ],
        "knowsAbout": [
          {
            "@type": "Thing",
            "@id": "https://www.wikidata.org/wiki/Q296260",
            "name": "Fútbol"
          },
          {
            "@type": "Thing",
            "@id": "https://es.wikipedia.org/wiki/F%C3%BAtbol",
            "name": "Fútbol"
          },
          {
            "@type": "Thing",
            "@id": "https://www.wikidata.org/wiki/Q847",
            "name": "Tenis"
          },
          {
            "@type": "Thing",
            "@id": "https://es.wikipedia.org/wiki/Tenis",
            "name": "Tenis"
          },
          {
            "@type": "Thing",
            "@id": "https://www.wikidata.org/wiki/Q5372",
            "name": "Baloncesto"
          },
          {
            "@type": "Thing",
            "@id": "https://es.wikipedia.org/wiki/Baloncesto",
            "name": "Baloncesto"
          }
        ],
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4",
          "ratingCount": "66",
          "bestRating": "5",
          "itemReviewed": {
            "@type": "Organization",
            "name": "Bet777"
          }
        }
      },
      {
        "@type": "Service",
        "@id": "https://www.bet777.es/#service",
        "serviceType": "Apuestas deportivas",
        "provider": {
          "@id": "https://www.bet777.es/#organization"
        },
        "areaServed": {
          "@type": "Country",
          "name": "España"
        },
        "offers": {
          "@type": "Offer",
          "url": "https://www.bet777.es/promociones/",
          "description": "Bonos y promociones exclusivas para jugadores de Bet777 en apuestas deportivas."
        }
      },
      {
        "@type": "WebSite",
        "@id": "https://www.bet777.es/#website",
        "url": "https://www.bet777.es/",
        "name": "Bet777",
        "publisher": {
          "@id": "https://www.bet777.es/#organization"
        }
      },
      {
        "@type": "WebPage",
        "@id": "https://www.bet777.es/#homepage",
        "url": "https://www.bet777.es/",
        "name": "Página principal de Bet777",
        "description": "Bet777 es tu casa de apuestas online en España. Apuesta en fútbol, tenis, baloncesto y muchos más deportes con cuotas competitivas y seguridad garantizada.",
        "isPartOf": {
          "@id": "https://www.bet777.es/#website"
        },
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "@id": "https://www.bet777.es/#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Inicio",
              "item": "https://www.bet777.es/"
            }
          ]
        },
        "mainEntity": {
          "@id": "https://www.bet777.es/#service"
        }
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "¿Qué tipos de apuestas deportivas puedo realizar en Bet777?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "En Bet777 puedes hacer apuestas simples, combinadas, de sistema, por hándicap, a largo plazo e incluso en deportes virtuales. También ofrecemos apuestas en vivo para los eventos más emocionantes."
            }
          },
          {
            "@type": "Question",
            "name": "¿Qué deportes están disponibles para apostar?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Puedes apostar en una amplia gama de disciplinas, incluyendo fútbol, tenis, baloncesto, hockey sobre hielo, rugby, béisbol, cricket, boxeo y muchos más."
            }
          },
          {
            "@type": "Question",
            "name": "¿Cómo se determinan las cuotas en Bet777?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Las cuotas reflejan la probabilidad que la casa de apuestas asigna a un resultado. En Bet777 ofrecemos cuotas competitivas, lo que significa que puedes obtener mayores beneficios por tus aciertos."
            }
          },
          {
            "@type": "Question",
            "name": "¿Cuánto tarda Bet777 en pagar?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Los resultados de cada evento se publican en el sitio web de Bet777 como máximo 24 horas de finalizado dicho evento. Bet777 se compromete a pagar los premios una vez publicados los resultados."
            }
          },
          {
            "@type": "Question",
            "name": "¿Cómo funciona el hándicap en las apuestas deportivas?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "En las apuestas deportivas, el hándicap es una ventaja o desventaja que se le da a un equipo o deportista frente a su rival. Se utiliza básicamente para igualar las posibilidades de ganar que tiene el no favorito frente al favorito."
            }
          },
          {
            "@type": "Question",
            "name": "¿Qué significa stake en apuestas deportivas?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Se utiliza el término stake en apuestas deportivas para referirse al nivel de confianza que el apostante tiene en que su pronóstico resultará acertado. Eso se traduce en la cantidad de dinero que se está dispuesto a invertir en cada apuesta."
            }
          },
          {
            "@type": "Question",
            "name": "¿Cómo funciona una apuesta combinada?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Una apuesta combinada incluye varias apuestas simples. Se computa como una sola apuesta y para conocer su cuota hay que multiplicar las cuotas de las apuestas simples que la integran. La ventaja es que se puede ganar mucho dinero con una inversión pequeña. Sin embargo, para que eso suceda hay que acertar todos los pronósticos."
            }
          },
          {
            "@type": "Question",
            "name": "¿Qué es el yield en las apuestas?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "En apuestas deportivas, el término yield refiere al porcentaje que indica cuánto dinero se ganó o se perdió en relación con la cantidad total invertida."
            }
          },
          {
            "@type": "Question",
            "name": "¿Qué son las unidades en las apuestas?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Las unidades son la manera de medir las cantidades de apuestas al realizar pronósticos deportivos. Es un porcentaje fijo del bankroll que, aunque lo define cada jugador, suele ser por lo general del 1%. La unidad de apuesta se utiliza para poder establecer un estándar que permita llevar adelante una gestión del riesgo consciente."
            }
          }
        ]
      }
    ]
  }
}

export function generateFootballPageSchema() {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": "https://www.bet777.es/futbol/#categorypage",
        "url": "https://www.bet777.es/futbol/",
        "name": "Apuestas de Fútbol en Bet777",
        "description": "Descubre todas las apuestas de fútbol en Bet777: Champions League, LaLiga, Premier League y más competiciones. Vive la emoción del mejor fútbol online.",
        "isPartOf": {
          "@id": "https://www.bet777.es/#website"
        },
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "@id": "https://www.bet777.es/futbol/#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Inicio",
              "item": "https://www.bet777.es/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Fútbol",
              "item": "https://www.bet777.es/futbol/"
            }
          ]
        },
        "mainEntity": {
          "@type": "ItemList",
          "@id": "https://www.bet777.es/futbol/#itemlist",
          "name": "Competiciones de fútbol en Bet777",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "url": "https://www.bet777.es/futbol/champions-league/",
              "name": "Champions League"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "url": "https://www.bet777.es/futbol/espana-la-liga/",
              "name": "LaLiga"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "url": "https://www.bet777.es/futbol/inglaterra-premier-league/",
              "name": "Premier League"
            }
          ]
        },
        "subjectOf": [
          {
            "@type": "Thing",
            "@id": "https://en.wikipedia.org/wiki/Football",
            "name": "Fútbol"
          },
          {
            "@type": "Thing",
            "@id": "https://www.wikidata.org/wiki/Q296260",
            "name": "Fútbol"
          }
        ]
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "¿Cómo realizar apuestas en fútbol?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Ingresa a Bet777 y en la sección de deportes elige Fútbol entre las opciones del menú del margen izquierdo. Se desplegarán los eventos en los que puedes realizar tus apuestas de fútbol. Recuerda que también puedes optar por realizar apuestas en partidos de fútbol en vivo."
            }
          },
          {
            "@type": "Question",
            "name": "¿Cuánto cuesta apostar en fútbol?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "En Bet777 la cantidad mínima de apuesta en fútbol es €0.10. La cantidad máxima de apuesta dependerá de las ligas o mercados y los tipos de apuesta."
            }
          },
          {
            "@type": "Question",
            "name": "¿Qué tipos de apuestas de fútbol existen?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Si bien la apuesta de fútbol más común es al ganador del partido, existen muchas otras opciones. También es posible pronosticar quién será el goleador, o realizar apuestas de fútbol por tiempos, por margen de victoria, apuestas de corner y mucho, muchísimo más."
            }
          },
          {
            "@type": "Question",
            "name": "¿Cuáles son las odds para fútbol?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Las odds son las probabilidades que tiene una apuesta deportiva de resultar exitosa. Y se relacionan directamente con el pago que se recibirá en el caso de que eso suceda. Se calculan según cada evento en particular y están disponibles al momento de realizar los pronósticos."
            }
          },
          {
            "@type": "Question",
            "name": "¿Cómo tener éxito realizando apuestas de fútbol online?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Para que las apuestas de fútbol tengan más probabilidades de ser exitosas conviene conocer lo máximo posible sobre este deporte y los equipos. Pero también realizar análisis que contemplen tanto las estadísticas como la situación actual de los jugadores y equipos. Por último, siempre se debe jugar con responsabilidad, sabiendo que existe una cuota de azar en los resultados finales."
            }
          },
          {
            "@type": "Question",
            "name": "¿Las apuestas de fútbol incluyen la prórroga y la tanda de penaltis?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "No, cuando apuestas al resultado de un partido solo se toma en consideración al resultado al final de los 90 minutos de juego reglamentario. Esto incluye cualquier tiempo adicional que se defina por lesiones u otros inconvenientes, pero deja afuera a la prórroga o los penaltis si los hubiera."
            }
          },
          {
            "@type": "Question",
            "name": "¿Qué pasa con mis apuestas si un partido de fútbol se suspende o se pospone?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Todas las apuestas realizadas en partidos que se abandonan, suspenden o cancelan son nulas si dichos encuentros no se repiten o se reprograman dentro de las 24 horas a partir de la hora de inicio original."
            }
          },
          {
            "@type": "Question",
            "name": "¿Cuándo y cómo apostar en la Champions League, la Eurocopa o el Mundial?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Realizar pronósticos en estas tres importantes competiciones funciona de la misma manera que con los demás torneos nacionales o internacionales. Deberás elegir el monto de tu apuesta y apostar por partido, por fase o por competencia total, teniendo en cuenta las variadas opciones disponibles. Para hacerlo, tendrás que esperar que el evento se encuentre próximo a desarrollarse, para así también contar con la información precisa de los equipos que intervendrán en cada caso."
            }
          }
        ]
      }
    ]
  }
}